/**
 * Çerez izni (KVKK) — analitik ve pazarlama izinleri ayrı ayrı tutulur.
 *
 * Tercih, Çerez Politikası'nda listelenen zorunlu "cookie_consent" çerezinde
 * "v1.a1.m0.<unix zamanı>" biçiminde saklanır (a = analitik, m = pazarlama), 12 ay geçerlidir;
 * zaman damgası iznin ne zaman verildiğini gösterir.
 * client/index.html bu çerezi sayfa açılırken okur: GA4 yalnızca analitik izniyle, Meta ve
 * OpenAI pikselleri yalnızca pazarlama izniyle yüklenir; izinsiz kategorinin çerezleri silinir.
 */

export interface CerezIzni {
  analitik: boolean;
  pazarlama: boolean;
  /** Kullanıcı bir seçim yaptı mı (banner gösterilsin mi) */
  secildi: boolean;
}

declare global {
  interface Window {
    __djIzin?: CerezIzni;
    __djIzinYukle?: () => void;
  }
}

const CEREZ_ADI = "cookie_consent";
const SURE_SN = 60 * 60 * 24 * 365;
export const IZIN_DEGISTI = "dj-izin";
export const PANELI_AC = "dj-izin-ac";

export function izniOku(): CerezIzni {
  if (typeof document === "undefined") return { analitik: false, pazarlama: false, secildi: false };
  const m = document.cookie.match(/(?:^|;\s*)cookie_consent=v1\.a([01])\.m([01])/);
  if (m) return { analitik: m[1] === "1", pazarlama: m[2] === "1", secildi: true };
  // Çerez yazılamadıysa (ör. tarayıcı engeli) bu sayfa görünümünde verilen seçim geçerlidir
  return window.__djIzin ?? { analitik: false, pazarlama: false, secildi: false };
}

export const pazarlamaIzniVar = () => izniOku().pazarlama;
export const analitikIzniVar = () => izniOku().analitik;

/** İzin geri alındığında, daha önce bırakılmış birinci taraf ölçüm çerezlerini siler */
function cerezleriSil(onekler: string[]) {
  const alanlar = ["", `; Domain=${location.hostname}`, `; Domain=.${location.hostname.replace(/^www\./, "")}`];
  for (const parca of document.cookie.split(";")) {
    const ad = parca.split("=")[0].trim();
    if (!onekler.some(o => ad === o || ad.startsWith(`${o}_`))) continue;
    for (const alan of alanlar) document.cookie = `${ad}=; Max-Age=0; Path=/${alan}`;
  }
}

export function izniKaydet(analitik: boolean, pazarlama: boolean) {
  const once = izniOku();
  const zaman = Math.floor(Date.now() / 1000);
  const guvenli = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CEREZ_ADI}=v1.a${analitik ? 1 : 0}.m${pazarlama ? 1 : 0}.${zaman}; Max-Age=${SURE_SN}; Path=/; SameSite=Lax${guvenli}`;
  const izin: CerezIzni = { analitik, pazarlama, secildi: true };
  window.__djIzin = izin;

  try {
    window.gtag?.("consent", "update", {
      analytics_storage: analitik ? "granted" : "denied",
      ad_storage: pazarlama ? "granted" : "denied",
      ad_user_data: pazarlama ? "granted" : "denied",
      ad_personalization: pazarlama ? "granted" : "denied",
    });
    if (pazarlama) {
      window.fbq?.("consent", "grant");
      // Bu sayfanın OpenAI görüntüleme ölçümü kuyruğa alınır; SDK yüklenince gönderilir
      if (!once.pazarlama) window.oaiq?.("measure", "page_viewed", { type: "contents" });
    } else {
      window.fbq?.("consent", "revoke");
    }
    if (analitik || pazarlama) window.__djIzinYukle?.();
  } catch {
    // ölçüm betikleri yüklenemediyse tercih yine kaydedilir
  }

  if (!analitik) cerezleriSil(["_ga"]);
  if (!pazarlama) cerezleriSil(["_gcl_au", "_fbp", "_fbc", "__oppref", "__obref"]);

  window.dispatchEvent(new CustomEvent(IZIN_DEGISTI, { detail: izin }));

  // Yüklenmiş ölçüm betikleri sayfa açıkken durdurulamaz; izin geri alındıysa sayfa yenilenir
  if ((once.analitik && !analitik) || (once.pazarlama && !pazarlama)) location.reload();
}

/** Footer'daki "Çerez Tercihleri" bağlantısı paneli yeniden açar */
export function tercihPaneliniAc() {
  window.dispatchEvent(new Event(PANELI_AC));
}
